import React from 'react';
import { Security, VpnKey, Lock, Code } from '@mui/icons-material';
import ParameterTable from "../components/documentation/ParameterTable";
import CodeBlock from "../components/documentation/CodeBlock";

const Authentication = () => {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-amber-100 rounded-2xl">
                            <Security className="text-4xl text-amber-600" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Authentification OAuth2</h1>
                    <p className="text-xl text-gray-600">
                        Sécurisez l'accès à vos APIs Netikash avec le protocole OAuth2
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    {/* Introduction */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Présentation</h2>
                        <p className="text-gray-700 leading-relaxed">
                            L'authentification OAuth2 permet à votre application d'accéder aux APIs Netikash
                            de manière sécurisée. Le flux "Client Credentials" est utilisé pour les authentifications
                            serveur-à-serveur sans interaction utilisateur.
                        </p>
                    </section>

                    {/* OAuth2 Flow */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <VpnKey className="mr-2 text-amber-600" />
                            Flux d'Authentification
                        </h2>

                        <div className="bg-gray-50 rounded-lg p-6 mb-6">
                            <div className="grid md:grid-cols-4 gap-4 text-center">
                                <div className="p-4 bg-white rounded-lg border border-amber-200">
                                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <span className="font-bold text-amber-600">1</span>
                                    </div>
                                    <h4 className="font-semibold text-sm">Credentials</h4>
                                    <p className="text-xs text-gray-600">Client ID & Secret</p>
                                </div>
                                <div className="p-4 bg-white rounded-lg border border-amber-200">
                                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <span className="font-bold text-amber-600">2</span>
                                    </div>
                                    <h4 className="font-semibold text-sm">Encodage</h4>
                                    <p className="text-xs text-gray-600">Base64 Encoding</p>
                                </div>
                                <div className="p-4 bg-white rounded-lg border border-amber-200">
                                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <span className="font-bold text-amber-600">3</span>
                                    </div>
                                    <h4 className="font-semibold text-sm">Requête</h4>
                                    <p className="text-xs text-gray-600">POST /oauth2/token</p>
                                </div>
                                <div className="p-4 bg-white rounded-lg border border-amber-200">
                                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <span className="font-bold text-amber-600">4</span>
                                    </div>
                                    <h4 className="font-semibold text-sm">Token</h4>
                                    <p className="text-xs text-gray-600">Access Token</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Endpoint */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Endpoint</h2>
                        <div className="bg-gray-900 rounded-lg p-4 mb-4">
                            <div className="flex items-center space-x-4">
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">POST</span>
                                <code className="text-green-400 font-mono">https://accounts.netikash.com/oauth2/token</code>
                            </div>
                        </div>
                    </section>

                    {/* Headers */}
                    <ParameterTable
                        title="Entêtes de Requête"
                        parameters={[
                            { name: 'Content-Type', type: 'application/json', required: 'oui', description: 'Type de contenu' },
                            { name: 'Authorization', type: 'Basic base64(client_id:client_secret)', required: 'oui', description: 'Credentials encodés en Base64' },
                            { name: 'Accept', type: 'application/json', required: 'non', description: 'Type de réponse attendu' }
                        ]}
                    />

                    {/* Request Body */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Corps de la Requête</h3>
                        <CodeBlock
                            code={JSON.stringify({
                                grant_type: "client_credentials"
                            }, null, 2)}
                            language="json"
                        />
                    </section>

                    {/* Response */}
                    <section className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Réponse</h3>
                        <CodeBlock
                            code={JSON.stringify({
                                access_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
                                token_type: "bearer",
                                expires_in: 3600,
                                scope: "payment:write payment:read"
                            }, null, 2)}
                            language="json"
                        />
                    </section>

                    {/* Code Examples */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <Code className="mr-2 text-amber-600" />
                            Exemples de Code
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Node.js</h4>
                                <CodeBlock
                                    code={`const axios = require('axios');
const { Buffer } = require('buffer');

const credentials = Buffer.from(\`\${clientId}:\${clientSecret}\`).toString('base64');

const response = await axios.post(
  'https://accounts.netikash.com/oauth2/token',
  { grant_type: 'client_credentials' },
  {
    headers: {
      'Authorization': \`Basic \${credentials}\`,
      'Content-Type': 'application/json'
    }
  }
);`}
                                    language="javascript"
                                />
                            </div>

                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Python</h4>
                                <CodeBlock
                                    code={`import requests
import base64

credentials = base64.b64encode(
  f"{client_id}:{client_secret}".encode()
).decode()

response = requests.post(
  'https://accounts.netikash.com/oauth2/token',
  json={'grant_type': 'client_credentials'},
  headers={
    'Authorization': 'Basic {credentials}',
    'Content-Type': 'application/json'
  }
)`}
                                    language="python"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Security Notes */}
                    <section className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-lg">
                        <h3 className="text-lg font-semibold text-amber-900 mb-2 flex items-center">
                            <Lock className="mr-2" />
                            Notes de Sécurité
                        </h3>
                        <ul className="text-amber-800 space-y-2">
                            <li>• Ne jamais exposer vos client_secret dans le code client</li>
                            <li>• Utilisez des variables d'environnement pour stocker les credentials</li>
                            <li>• Validez toujours la durée de vie du token (expires_in)</li>
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Authentication;